import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import uploadService from "./uploadService";

/* ===================== IMAGES ===================== */

// Upload multiple images
export const uploadImg = createAsyncThunk(
  "upload/images",
  async (files, thunkAPI) => {
    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("images", file)); // append each image
      const response = await uploadService.uploadImg(formData);
      return response; // expected: array of uploaded image objects
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Image upload failed"
      );
    }
  }
);

// Delete a single image by Cloudinary public_id
export const delImg = createAsyncThunk(
  "delete/images",
  async (id, thunkAPI) => {
    try {
      const response = await uploadService.deleteImg(id);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Image deletion failed"
      );
    }
  }
);

/* ===================== VIDEO ===================== */

// Upload a single video
export const uploadVideo = createAsyncThunk(
  "upload/video",
  async (file, thunkAPI) => {
    try {
      const formData = new FormData();

      // 👇 SINGLE video (no loop)
      formData.append("video", file);

      return await uploadService.uploadVideo(formData);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Video upload failed"
      );
    }
  }
);


// Delete a video by Cloudinary public_id
export const deleteVideo = createAsyncThunk(
  "delete/video",
  async (id, thunkAPI) => {
    try {
      const response = await uploadService.deleteVideo(id);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message || "Video deletion failed"
      );
    }
  }
);

/* ===================== SLICE ===================== */

const initialState = {
  images: [],       // array of image objects
  video: null,      // single video object { public_id, url, asset_id }
  isError: false,
  isLoading: false,
  isSuccess: false,
  message: "",
};

export const uploadSlice = createSlice({
  name: "upload",
  initialState,
  reducers: {
    resetUploadState: (state) => {
      state.isError = false;
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      /* ===== IMAGES ===== */
      .addCase(uploadImg.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(uploadImg.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.images = action.payload; // array of uploaded images
      })
      .addCase(uploadImg.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload || action.error.message;
      })
      .addCase(delImg.fulfilled, (state) => {
        state.images = []; // clear all images after deletion
      })

      /* ===== VIDEO ===== */
      .addCase(uploadVideo.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = "";
      })
      .addCase(uploadVideo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isError = false;
        state.isSuccess = true;
        state.video = action.payload; // single video object
      })
      .addCase(uploadVideo.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.message = action.payload || action.error.message;
      })
      .addCase(deleteVideo.fulfilled, (state) => {
        state.video = null;
      });
  },
});

export const { resetUploadState } = uploadSlice.actions;
export default uploadSlice.reducer;
